import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import LoadingButton from "../../../components/LoadingButton";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Link from "@material-ui/core/Link";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
} from "@material-ui/core";
import { callAPI } from "../../../lib/common";
import * as PropTypes from "prop-types";
import BoxActions from "./BoxActions";
import { usePortal } from "../../../Providers/PortalProvider";
import Level2BoxContainer from "./Level2BoxContainer";
import FormikTextField from "../../../components/Common/FormikTextField";
import Level2BoxHeading from "./Level2BoxHeading";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import { useAuth } from "../../providers/AuthProvider";
import { getYUPValidators } from "../../../components/yupvalidators";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import InputMobileNumber from "../../Desktop/Pages/SubjectRegistration/Components/InputMobileNumber";
import FormControl from "@material-ui/core/FormControl";

const useStyles = makeStyles((theme) => ({
  form: {
    marginBottom: 0,
  },
  container: {
    marginBottom: theme.spacing(3),
  },
  termsOfServiceText: {
    textAlign: "center",
    fontWeight: 400,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& > a": {
      ...theme.typography.button,
      padding: 0,
      minWidth: 0,
      display: "inline",
    },
  },
  acceptMail: {
    marginTop: theme.spacing(2),
    "& .MuiTypography-body1": {
      fontSize: "12px",
    },
  },
}));

Level2BoxHeading.propTypes = { children: PropTypes.node };
export default ({ email, onLoggedIn, subjectInfo, handleGoBack }) => {
  const [error, setError] = useState("");
  const { t } = useTranslation();
  const classes = useStyles();
  const [termsOfServiceOpen, setTermsOfServiceOpen] = useState(false);
  const isMobile = useIsMobileDevice();
  const { isAuthenticated, loadCurrentUser, user } = useAuth();
  const portal = usePortal();

  useEffect(() => {
    if (isAuthenticated) {
      if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
        window.epmsAnalytics.onRegisterUser(user);
      }
      onLoggedIn();
    }
  }, [isAuthenticated]);

  const openTermsOfService = (e) => {
    e.preventDefault();
    setTermsOfServiceOpen(true);
  };

  const initialValues = {
    mobile: "",
    password: "",
    acceptmail: false,
  };

  if (portal.options.acceptMailDefaultOn) {
    initialValues.acceptmail = true;
  }

  if (portal.options.disableAcceptMailOptionWhenRegisteringUser) {
    initialValues.acceptmail = null;
  }

  const acceptMailLabel = portal.options.acceptMailDefaultOn
    ? "Yes, I agree to receive relevant offers by email. This applies only to the images I have been given secure access to from %s"
    : "Yes, I agree to receive promotional e-mails and SMS from %s";

  return (
    <Level2BoxContainer className={classes.container}>
      <Level2BoxHeading color="primary">
        <Trans>Create a free user account</Trans>
      </Level2BoxHeading>

      <Formik
        initialValues={initialValues}
        validationSchema={SignupSchema(t)}
        onSubmit={(values, { setSubmitting, setErrors }) => {
          setSubmitting(true);
          callAPI("/ajax/create.php", "POST", {
            ...values,
            email,
            code: subjectInfo.code,
            acceptmail: values.acceptmail,
            confirm_password: values.password,
            termsofservice: true,
          })
            .then((res) => {
              if (res.error) {
                setError(res.error);
                setErrors(
                  res.data.reduce((errors, error) => {
                    errors[error] = true;
                    return errors;
                  }, {}),
                );
              } else {
                loadCurrentUser();
              }
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({
          submitForm,
          isSubmitting,
          isValid,
          errors,
          values,
          handleChange,
        }) => {
          console.log("Values", values);
          return (
            <Form className={classes.form}>
              <Field
                id="register-email-address"
                component={FormikTextField}
                label={t("Email address")}
                disabled={true}
                name="email"
                variant="outlined"
                fullWidth
                value={email}
                icon="mail_outline"
              />

              <Field
                id="register-mobile-phone"
                component={InputMobileNumber}
                name="mobile"
                label={t("Phone number")}
                margin="normal"
                fullWidth
              />

              <Field
                id="register-password"
                component={FormikTextField}
                type="password"
                label={t("Choose a password")}
                name="password"
                variant="outlined"
                fullWidth
                icon="lock_outline"
              />

              {!portal.options.disableAcceptMailWhenLoggingIn && (
                <FormControlLabel
                  id="register-accept-mail"
                  control={
                    <Checkbox
                      name="acceptmail"
                      checked={Boolean(values.acceptmail)}
                      onChange={handleChange}
                    />
                  }
                  label={t(acceptMailLabel, {
                    postProcess: "sprintf",
                    sprintf: [portal.name],
                  })}
                  className={classes.acceptMail}
                />
              )}
              <p>
                <FormHelperText error={!!error}>{error}</FormHelperText>
              </p>

              <Box mt={1} mb={1}>
                <Typography
                  variant="body1"
                  className={classes.termsOfServiceText}
                >
                  <Trans>
                    By signing up, I agree to the{" "}
                    <Link onClick={openTermsOfService} color="primary">
                      Terms of service
                    </Link>
                  </Trans>
                </Typography>
              </Box>

              <BoxActions>
                {!isMobile && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleGoBack}
                  >
                    <Trans>Go back</Trans>
                  </Button>
                )}
                <LoadingButton
                  variant="primary"
                  isLoading={isSubmitting}
                  onClick={submitForm}
                  disabled={!isValid}
                  fullWidth
                >
                  <Trans>Create user</Trans>
                </LoadingButton>
              </BoxActions>
            </Form>
          );
        }}
      </Formik>
      {termsOfServiceOpen && (
        <TermsOfServiceDialog onClose={() => setTermsOfServiceOpen(false)} />
      )}
    </Level2BoxContainer>
  );
};

const TermsOfServiceDialog = ({ onClose }) => {
  const { terms } = usePortal();

  return (
    <Dialog open={true}>
      <DialogTitle>
        <Trans>Terms of service</Trans>
      </DialogTitle>
      <DialogContent>
        <div
          dangerouslySetInnerHTML={{
            __html: terms.termsOfService.replace(/(?:\r\n|\r|\n)/g, "<br/>"),
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          <Trans>Ok</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SignupSchema = (t) => () => {
  const validators = getYUPValidators(t);
  return Yup.object().shape({
    mobile: validators.MOBILE_PHONE_WITH_SERVER_VALIDATION,
    password: Yup.string()
      .min(6, t("That's too short!"))
      .required(t("This is required!")),
  });
};
